import {
  Box,
  Columns,
  Heading,
  PresentationLink,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  display,
  height,
  marginLeft,
  match,
  paddingX,
  width,
} from '@spaceship-fspl/styles';
import Link from 'next/link';
import * as React from 'react';
import styled from 'styled-components';

import SuperBenefit1 from './img-super-benefit-1.svg';
import SuperBenefit2 from './img-super-benefit-2.svg';
import SuperBenefit3 from './img-super-benefit-3.svg';
import SuperBenefit4 from './img-super-benefit-4.svg';

interface ReasonsPanelProps {
  reasons: {
    title?: string | null;
    tagline?: string | null;
    learnMoreLink?: string | null;
  }[];
}

export const BenefitWrapper = styled.div`
  display: flex;
  height: unset;
  flex-direction: row;
  ${height({ md: '506px', lg: '760px' })}
`;

const BenefitText = styled.div<{ isEven: boolean }>`
  ${display({ xs: 'block', md: 'flex' })}
  ${paddingX({ xs: 'sm', md: 'none' })}
  ${width({ xs: '100%', md: '50%' })}
  align-self: center;
  justify-content: ${({ isEven }) => (isEven ? 'flex-start' : 'flex-end')};
  order: ${({ isEven }) => (isEven ? 2 : 1)};
`;

const BenefitTextInnerWrapper = styled.div<{ isEven: boolean }>`
  max-width: 600px;
  ${({ isEven }) =>
    isEven
      ? marginLeft({ xs: 'none', md: 'md' })
      : marginLeft({ xs: 'none', md: 'xl' })}
`;

const BenefitImageWrapper = styled.div<{ isEven: boolean }>`
  ${display({ xs: 'none', md: 'flex' })}
  width: 50%;
  align-items: center;
  justify-content: center;
  order: ${({ isEven }) => (isEven ? 1 : 2)};
  overflow: hidden;
  ${match('xl')<{ isEven: boolean }>`
    justify-content: ${({ isEven }) => (isEven ? 'flex-start' : 'flex-end')};
  `};
`;

const BenefitImage = styled.img<{ isEven: boolean }>`
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;

  ${match('xl')`
    margin-right: 0;
    margin-left: 0;
  `};
`;

const BenefitMobileImageWrapper = styled.div`
  height: 380px;
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const BenefitImageSources = [
  SuperBenefit1,
  SuperBenefit2,
  SuperBenefit3,
  SuperBenefit4,
];

export const ReasonsPanel: React.FC<
  React.PropsWithChildren<ReasonsPanelProps>
> = ({ reasons }) => {
  return (
    <Box
      as="section"
      backgroundColor="neutral.000"
      marginTop="xxl"
      display="flex"
      justifyContent="center"
    >
      <Box maxWidth="1440px">
        <Stack spaceY={{ xs: 'xl', md: 'none' }}>
          {reasons.map(({ title, tagline, learnMoreLink }, index) => {
            const isEven = (index + 1) % 2 === 0;
            return (
              <Stack
                spaceY={{ xs: 'sm', md: 'none' }}
                key={`${title}-${index}`}
              >
                <BenefitWrapper>
                  <BenefitText isEven={isEven}>
                    <BenefitTextInnerWrapper isEven={isEven}>
                      <Columns
                        alignX={{ md: 'center', lg: isEven ? 'left' : 'right' }}
                      >
                        <Columns.Column width={{ md: 0.95, lg: 5 / 6 }}>
                          <Stack spaceY={{ xs: 'sm', lg: 'md' }}>
                            <Heading
                              variant={2}
                              color="neutral.100"
                              align={{ xs: 'center', md: 'left' }}
                              component="h2"
                            >
                              {title}
                            </Heading>
                            <Text
                              variant={1}
                              color="neutral.085"
                              align={{ xs: 'center', md: 'left' }}
                            >
                              {tagline}
                            </Text>
                            {learnMoreLink ? (
                              <Link
                                href={learnMoreLink}
                                passHref={true}
                                legacyBehavior
                              >
                                <PresentationLink size="md" icon="arrow">
                                  Learn more
                                </PresentationLink>
                              </Link>
                            ) : null}
                          </Stack>
                        </Columns.Column>
                      </Columns>
                    </BenefitTextInnerWrapper>
                  </BenefitText>
                  <BenefitImageWrapper isEven={isEven}>
                    <BenefitImage
                      src={BenefitImageSources[index]}
                      alt=""
                      isEven={isEven}
                    />
                  </BenefitImageWrapper>
                </BenefitWrapper>
                <Visible
                  isHidden={{ xs: false, md: true }}
                  displayValue="block"
                >
                  <BenefitMobileImageWrapper>
                    <BenefitImage
                      src={BenefitImageSources[index]}
                      alt=""
                      isEven={isEven}
                    />
                  </BenefitMobileImageWrapper>
                </Visible>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};
